/**
 * Module 32 (WP-UI.1) — Layout-mode controller.
 *
 * One reactive signal for portrait/landscape that CSS, React HUDs, and Pixi canvases all
 * read from the same source of truth. This replaces the per-surface, computed-once-at-mount
 * orientation checks (e.g. Water Sort's `detectLockedOrientation`) with an app-wide signal
 * published as a `data-layout="portrait|landscape"` attribute on the document root.
 *
 * Layout mode is a pure function of the **viewport aspect ratio**, on every platform:
 * `innerWidth < innerHeight ? 'portrait' : 'landscape'` (a square viewport resolves to
 * landscape), recomputed live and debounced on resize/orientationchange.
 *
 * Module 47 (WP-47.1) — it did NOT used to be. Mobile hard-returned `portrait` and attached
 * no listeners, encoding the product rule "mobile is portrait-locked" inside the engine. That
 * rule is an **app-level** decision, and one host does not share it: the CrazyGames embed
 * declares `orientation: 'both'` and bans `RotateToPortraitOverlay` outright, so a phone held
 * in landscape got the portrait layout's reserves (88 + 104 + 84px) subtracted from a 360px-tall
 * viewport — an ~84px board with the HUD on top of it. The engine now answers only the
 * geometric question; walling off mobile landscape is the overlay's job on the hosts that want
 * it (see `RotateToPortraitOverlay`, which keys off `getPlatformClass()` and its own listeners,
 * never off this signal).
 *
 * `getPlatformClass()` is unchanged and still exported — it answers "what kind of device is
 * this", which is a different question from "which way is it being held".
 *
 * LAYOUT ONLY — this module changes no visuals. It only decides which of the two layout
 * modes is active and notifies subscribers when the viewport is resized/rotated.
 */
/** Attribute the active mode is published under, on the document root element. */
export const LAYOUT_ATTR = 'data-layout';
/**
 * Attribute the resolved platform class is published under, beside {@link LAYOUT_ATTR}.
 *
 * Module 47 (WP-47.1) — a diagnostic, deliberately not styled against by anything: the CSS
 * consumes `data-layout` and only `data-layout`. It exists because the bug this module fixes
 * shipped past two QA passes that drove phone-sized viewports in desktop Chromium, where
 * `getPlatformClass()` silently resolved to `desktop` and the checks took the branch that was
 * never broken. Publishing the memoized answer makes "which branch did this run actually
 * exercise" observable to a test rather than something it has to re-derive and hope matches.
 */
export const PLATFORM_ATTR = 'data-platform';
/**
 * Module 120 (WP-120.3) — the UI scale, published as a CSS custom property on the document root
 * beside {@link LAYOUT_ATTR}. Every HUD group that reads it (`zoom: var(--ui-scale)`) and every
 * board reserve that must clear those groups multiplies by the SAME number.
 */
export const UI_SCALE_VAR = '--ui-scale';
/** Present (value `1`) on the document root whenever {@link UI_SCALE_VAR} is above 1. */
export const UI_SCALED_ATTR = 'data-ui-scaled';
/**
 * Module 120 — the reference screen the HUD was designed on (an iPhone 14), per orientation.
 * Like Unity's Canvas Scaler "Scale With Screen Size": a screen this size or smaller is 1.
 */
export const UI_SCALE_REF = {
    portrait: { width: 390, height: 844 },
    landscape: { width: 844, height: 390 },
};
/** The ceiling: a 2560px monitor gets the same HUD as a 1920px one (mockup v4, approved). */
export const UI_SCALE_MAX = 1.8;
/** Below this short side the screen is a phone and the scale is exactly 1 (phones pixel-identical). */
export const UI_SCALE_MIN_SHORT_SIDE = 500;
/**
 * Landscape only: what the Top Bar plus the tallest rail need at scale 1 (a 38px allowance for
 * the screen's own edges + 496px of bar and rail). The scale is capped so they still fit the
 * height: 1366x768 → 1.47, not the 1.62 the width would give. Measured in the Module 120 matrix.
 */
export const UI_SCALE_LAND_FIT = { allowance: 38, content: 496 };
/**
 * Module 120 (WP-120.3) — the pure UI-scale decision. Phones (short side < 500) are exactly 1;
 * anything bigger scales by the smaller of its width and height ratios to the reference, capped
 * at {@link UI_SCALE_MAX} and, in landscape, by {@link UI_SCALE_LAND_FIT}. Never below 1.
 */
export function computeUiScale(viewport) {
    const { width: w, height: h } = viewport;
    if (!(w > 0 && h > 0) || Math.min(w, h) < UI_SCALE_MIN_SHORT_SIDE)
        return 1;
    const land = w >= h;
    const ref = land ? UI_SCALE_REF.landscape : UI_SCALE_REF.portrait;
    let s = Math.min(UI_SCALE_MAX, w / ref.width, h / ref.height);
    if (land)
        s = Math.min(s, (h - UI_SCALE_LAND_FIT.allowance) / UI_SCALE_LAND_FIT.content);
    return Math.max(1, Math.round(s * 1000) / 1000);
}
let currentUiScale = 1;
/** The UI scale last published on the document root (1 before {@link initLayoutMode}). */
export function getUiScale() {
    return currentUiScale;
}
function publishUiScale(scale) {
    currentUiScale = scale;
    if (typeof document === 'undefined')
        return;
    const root = document.documentElement;
    root.style.setProperty(UI_SCALE_VAR, String(scale));
    if (scale > 1)
        root.setAttribute(UI_SCALED_ATTR, '1');
    else
        root.removeAttribute(UI_SCALED_ATTR);
}
/** Debounce for resize/orientationchange recomputes (ms). */
const RESIZE_DEBOUNCE_MS = 150;
/**
 * Detect the platform class from live globals, once per call (no memoization here so tests
 * can stub globals and re-detect). Precedence:
 *   1. Capacitor native flag — the app is running inside a native Android/iOS shell.
 *   2. Coarse pointer — a touch-primary device with no fine pointer (phones/tablets).
 *   3. User-agent fallback — for browsers where `matchMedia` is unavailable/unreliable.
 * Anything else is treated as desktop.
 *
 * Capacitor is read off the injected `window.Capacitor` global rather than importing
 * `@capacitor/core`, so `packages/ui` (shared by the game packages) takes no Capacitor dep.
 */
export function detectPlatformClass() {
    if (typeof window === 'undefined')
        return 'desktop';
    const cap = window.Capacitor;
    if (cap?.isNativePlatform?.())
        return 'mobile';
    if (typeof window.matchMedia === 'function') {
        try {
            if (window.matchMedia('(pointer: coarse)').matches)
                return 'mobile';
        }
        catch {
            // matchMedia present but threw on this query — fall through to the UA check.
        }
    }
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua))
        return 'mobile';
    return 'desktop';
}
let cachedPlatform;
/** Memoized platform class — detected once for the app's lifetime. */
export function getPlatformClass() {
    if (cachedPlatform === undefined)
        cachedPlatform = detectPlatformClass();
    return cachedPlatform;
}
/**
 * Pure layout-mode decision: aspect-ratio driven on **every** platform class, with a square
 * viewport (width === height) resolving to landscape.
 *
 * `platform` is retained in the signature deliberately. It is not dead: it keeps every call
 * site (and every test row) stating which device class it is describing, so a future rule that
 * genuinely needs the distinction — a tablet breakpoint, say — has somewhere to go without a
 * second signature change rippling through the packages that consume this. What it must never
 * again do is decide the *orientation*: see the module header for what that cost.
 */
export function computeLayoutMode(_platform, viewport) {
    return viewport.width < viewport.height ? 'portrait' : 'landscape';
}
/**
 * Module 89 (WP-89.7) — is this a landscape area too SHORT to play in?
 *
 * A measured-space rule, never a device rule. Inside a portal page a phone held
 * sideways keeps ~240 CSS px of height after the browser's and the portal's
 * bars (measured on Android Chrome inside playgama.ai, 2026-10-04): about
 * 24–28px per cell, against the ~44px a drag needs, under any layout. The same
 * phone in true fullscreen, a tablet, or a laptop is tall enough and keeps the
 * landscape layout. So the question is the area's height, not what the device
 * is, which is exactly what `RotateToPortraitOverlay` (mobile class + wider
 * than tall) gets wrong for a 'both' host.
 *
 * Square counts as landscape here, as it does in {@link computeLayoutMode}.
 */
export function isLandscapeTooShort(viewport, minHeight) {
    return viewport.width >= viewport.height && viewport.height < minHeight;
}
function currentViewport() {
    if (typeof window === 'undefined')
        return { width: 0, height: 0 };
    return { width: window.innerWidth, height: window.innerHeight };
}
const listeners = new Set();
let currentMode = 'portrait';
let initialized = false;
let debounceTimer;
let removeWindowListeners;
/** The current layout mode. Non-React consumers (Pixi) read this for their initial value. */
export function getLayoutMode() {
    return currentMode;
}
/**
 * Subscribe to layout-mode changes. Does NOT fire immediately — read `getLayoutMode()` for
 * the current value, then let subscribers update on the next change. Returns an unsubscribe
 * function. Shaped to satisfy React's `useSyncExternalStore` subscribe contract as well as
 * plain Pixi-canvas usage.
 */
export function subscribeLayoutMode(cb) {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}
function publish(mode) {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute(LAYOUT_ATTR, mode);
    }
    if (mode === currentMode)
        return;
    currentMode = mode;
    for (const cb of listeners)
        cb(mode);
}
/** Recompute the mode from live globals and publish if it changed. */
export function recomputeLayoutMode() {
    const viewport = currentViewport();
    // Module 120 — the scale first, so a mode subscriber that measures sees the new one.
    publishUiScale(computeUiScale(viewport));
    publish(computeLayoutMode(getPlatformClass(), viewport));
}
function onResize() {
    if (debounceTimer !== undefined)
        clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        debounceTimer = undefined;
        recomputeLayoutMode();
    }, RESIZE_DEBOUNCE_MS);
}
/**
 * Initialize the controller: compute the initial mode, publish the `data-layout` attribute,
 * and attach debounced resize/orientationchange listeners so the mode reflows live.
 * Idempotent.
 *
 * WP-47.1 — the listeners used to be gated on `getPlatformClass() === 'desktop'`, on the
 * reasoning that "mobile is portrait-locked, so it needs no listeners". That made the bug
 * un-recoverable rather than merely wrong: even once a phone WAS in landscape, nothing
 * re-evaluated, so the mode could not have corrected itself on the next rotation either.
 *
 * Both events are listened for because neither is sufficient alone. `orientationchange` fires
 * on some mobile browsers *before* `innerWidth`/`innerHeight` update, and `resize` does not
 * fire at all on a few older Android WebViews. The debounce restarts on every event, so a
 * rotation that emits both recomputes once, 150 ms after the last one — by which point the
 * viewport has settled.
 */
export function initLayoutMode() {
    if (initialized)
        return;
    initialized = true;
    // Seed `currentMode` without early-returning in `publish` (its no-op-on-equal guard would
    // otherwise skip the very first attribute write when the computed mode is 'portrait').
    currentMode = 'portrait';
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute(LAYOUT_ATTR, currentMode);
        document.documentElement.setAttribute(PLATFORM_ATTR, getPlatformClass());
    }
    recomputeLayoutMode();
    if (typeof window !== 'undefined') {
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        removeWindowListeners = () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }
}
/**
 * Test-only teardown: clears memoized platform, listeners, timers, and the initialized flag
 * so each test starts from a clean signal. Not used in production code paths.
 */
export function resetLayoutModeForTest() {
    if (debounceTimer !== undefined)
        clearTimeout(debounceTimer);
    debounceTimer = undefined;
    removeWindowListeners?.();
    removeWindowListeners = undefined;
    listeners.clear();
    cachedPlatform = undefined;
    currentMode = 'portrait';
    currentUiScale = 1;
    initialized = false;
    if (typeof document !== 'undefined') {
        document.documentElement.removeAttribute(LAYOUT_ATTR);
        document.documentElement.removeAttribute(PLATFORM_ATTR);
        document.documentElement.removeAttribute(UI_SCALED_ATTR);
        document.documentElement.style.removeProperty(UI_SCALE_VAR);
    }
}
